import { Drawer, Stack, Text, UnstyledButton, Group, Divider } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import Icon from './Icon';
import RestrictedMenuMessageModal from '../neoverv/components/Module/General/CustomModal/RestrictedMenuMessageModal';
import Autoload from '../neoverv/Autoload';
import useMobileTabRows from '../helpers/useMobileTabRows';
import './MobileTabBar.css';

const ACTIVE = 'rgba(86, 85, 255, 1.00)';
const INACTIVE = 'rgba(154, 154, 181, 1.00)';

// The four modules people use most get a tab; everything else from the desktop
// sidebar lives behind "Mehr", in the same order as the sidebar.
const TABS = [
  { label: 'Übersicht', icon: 'dashboard', route: '/dashboard', restricted: false },
  { label: 'Immobilie', icon: 'home', route: '/property', restricted: true },
  { label: 'Mieter', icon: 'users', route: '/user', restricted: true },
  { label: 'Buchhaltung', icon: 'receipt', route: '/accounting', restricted: true },
];

const MORE_ITEMS = [
  { label: 'Ablesungen', icon: 'meter', route: '/meter', restricted: true },
  { label: 'Betriebskosten', icon: 'tap', route: '/utility-billing', restricted: true },
  { label: 'Nachrichten', icon: 'messenger', route: '/messaging', restricted: true },
  { label: 'Dokumente', icon: 'documents', route: '/documents', restricted: true },
  { label: 'Mieterportal', icon: 'tenantPortal', route: '/tenantPortal', restricted: true },
];

// Bottom navigation for phones and portrait tablets: a fixed tab bar plus a
// bottom sheet with the rest of the menu. Replaces the old horizontally
// scrolling strip, where only four of twelve entries were visible.
const MobileTabBar = ({ history, blockUsersFromUsingApp, onLogOut, resetData, setShowAfterSignIn, isWegModeActive }) => {
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  useMobileTabRows(pathname, true);
  const [openRestrictedMenuMessage, setOpenRestrictedMenuMessage] = useState(false);

  // Intercom's launcher sits above every z-index we have, on top of the sheet's rows.
  useEffect(() => {
    if (!moreOpen) return undefined;
    window.Intercom?.('update', { hide_default_launcher: true });
    return () => window.Intercom?.('update', { hide_default_launcher: false });
  }, [moreOpen]);

  const isActive = (route) => !!matchPath(pathname, { path: route });
  const moreActive = MORE_ITEMS.some((item) => isActive(item.route)) || isActive('/profile');

  const navigate = (item) => {
    setMoreOpen(false);
    if (item.restricted && blockUsersFromUsingApp) {
      setOpenRestrictedMenuMessage(true);
      history.push('/dashboard');
      return;
    }
    history.push(item.route);
  };

  const handleLogout = () => {
    onLogOut && onLogOut();
    Autoload.Utils.clear_session_data();
    window.Intercom && window.Intercom('shutdown');
    resetData && resetData();
    window.location.href = '/login';
    setShowAfterSignIn && setShowAfterSignIn(true);
  };

  const SheetRow = ({ icon, label, active, color, onClick }) => (
    <UnstyledButton onClick={onClick} className={`mobile-tabbar-sheet-row ${active ? 'active' : ''}`}>
      <Group gap={14} wrap="nowrap">
        <Icon name={icon} size={18} color={color || (active ? ACTIVE : INACTIVE)} />
        <Text fw={500} size="15px" c={color || (active ? ACTIVE : '#13135C')}>{label}</Text>
      </Group>
    </UnstyledButton>
  );

  return (
    <>
      <nav className="mobile-tabbar" aria-label="Hauptnavigation">
        {TABS.map((tab) => {
          const active = isActive(tab.route);
          return (
            <UnstyledButton key={tab.route} className={`mobile-tabbar-item ${active ? 'active' : ''}`} onClick={() => navigate(tab)}>
              <Icon name={tab.icon} size={20} color={active ? ACTIVE : INACTIVE} />
              <span>{tab.label === 'Mieter' && isWegModeActive ? 'Mieter/Eig.' : tab.label}</span>
            </UnstyledButton>
          );
        })}
        <UnstyledButton className={`mobile-tabbar-item ${moreActive || moreOpen ? 'active' : ''}`} onClick={() => setMoreOpen(true)} aria-haspopup="dialog">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            {[4, 10, 16].map((y) => (
              <rect key={y} x="3" y={y - 1} width="14" height="2" rx="1" fill={moreActive || moreOpen ? ACTIVE : INACTIVE} />
            ))}
          </svg>
          <span>Mehr</span>
        </UnstyledButton>
      </nav>

      <Drawer
        opened={moreOpen}
        onClose={() => setMoreOpen(false)}
        position="bottom"
        size="auto"
        withCloseButton={false}
        zIndex={1000000}
        classNames={{ content: 'mobile-tabbar-sheet' }}
      >
        <div className="mobile-tabbar-sheet-handle" />
        <Stack gap={2}>
          {MORE_ITEMS.map((item) => (
            <SheetRow key={item.route} icon={item.icon} label={item.label} active={isActive(item.route)} onClick={() => navigate(item)} />
          ))}
        </Stack>
        <Divider my={8} color="#E9E9F1" />
        <Stack gap={2}>
          <SheetRow icon="help" label="Hilfe" onClick={() => { setMoreOpen(false); window.Intercom && window.Intercom('show'); }} />
          <SheetRow icon="user" label="Konto" active={isActive('/profile')} onClick={() => navigate({ route: '/profile', restricted: false })} />
          <SheetRow icon="logout" label="Abmelden" color="#E8193C" onClick={handleLogout} />
        </Stack>
      </Drawer>

      {openRestrictedMenuMessage &&
      <RestrictedMenuMessageModal
      open={openRestrictedMenuMessage}
      toggle={() => setOpenRestrictedMenuMessage(false)}
      history={history}
      />}
    </>
  );
};

export default MobileTabBar;
